/*
 * The frame is the fixed part; the scroll port inside it is the moving part.
 * Keeping them separate is what lets the faded edges hold still at the border
 * while the columns travel underneath, and it gives the sticky header a
 * scrollport of its own to stick to.
 */
.frame {
  position: relative;
  isolation: isolate;

  background-color: var(--surface);

  border: 1px solid var(--border);
  border-radius: var(--radius-m);
}

/*
 * The height cap is what makes the header sticky at all: without it the page
 * scrolls and the table has nothing to stick inside. It sits high enough that
 * an ordinary list of a dozen rows never reaches it, so only the long tables
 * (the catalog, the answers, the change log) scroll in place.
 */
.scroll {
  overflow: auto;

  max-block-size: min(38rem, 72vh);

  border-radius: inherit;

  /* The scrollbar is part of the table, not a browser leftover. */
  scrollbar-color: var(--border-strong) transparent;
  scrollbar-width: thin;
}

.table {
  width: 100%;

  border-collapse: separate;
  border-spacing: 0;

  font-size: var(--size-200);
}

/*
 * Once the reader has taken the columns over, the browser stops negotiating
 * widths: every column but the last holds what it was given, the last takes the
 * slack, and a value too long for its column wraps instead of pushing out.
 */
.sized {
  table-layout: fixed;
}

.sized .cell {
  overflow-wrap: anywhere;
}

/* Nothing selects while a column is being dragged; the drag is not a text gesture. */
.table[data-dragging] {
  user-select: none;
}

.head {
  position: sticky;
  top: 0;
  z-index: 2;

  padding: var(--space-xs) var(--space-s);

  text-align: start;
  font-size: var(--size-100);
  font-weight: var(--weight-medium);

  color: var(--text-muted);
  background-color: var(--surface-sunken);
  border-bottom: 1px solid var(--border-strong);
}

/*
 * The header earns its shadow only once rows have passed under it. Before that
 * it is simply the top of the table, and a shadow would be decoration.
 */
.frame[data-lifted] .head {
  box-shadow: 0 10px 14px -10px rgb(0 0 0 / 85%);
}

/*
 * The drag handle: a wide target, a hairline mark. It shows itself on approach
 * and on focus, because a control visible in every header of every table would
 * out-shout the data it serves.
 */
.grip {
  position: absolute;
  inset-block: 0;
  inset-inline-end: 0;
  z-index: 1;

  inline-size: 11px;

  cursor: col-resize;
  touch-action: none;
}

.grip::after {
  content: "";

  position: absolute;
  inset-block: 25%;
  inset-inline-end: 5px;

  inline-size: 1px;

  background-color: var(--border-strong);

  transition:
    background-color var(--duration-fast) var(--ease),
    inset-block var(--duration-fast) var(--ease);
}

.grip:hover::after,
.grip:focus-visible::after,
.grip[data-active]::after {
  inset-block: 0;

  background-color: var(--accent-strong);
}

.grip:focus-visible {
  outline: none;
}

.grip:focus-visible::after {
  inline-size: 2px;
}

.cell {
  padding: var(--space-xs) var(--space-s);

  vertical-align: top;

  border-bottom: 1px solid var(--border);
}

/* Kept clear of the viewport edge for a screen that scrolls a row back into view. */
.row {
  scroll-margin-block: var(--space-xl);
}

/* On a dark table a highlight can only go one way: up, towards the light. */
.row:hover .cell {
  background-color: rgb(255 255 255 / 4%);
}

/* The row the URL points at, so a reader scrolled back to it can tell which. */
.row:target .cell {
  background-color: var(--accent-soft);
}

.row:last-child .cell {
  border-bottom: none;
}

.end {
  text-align: end;

  font-variant-numeric: tabular-nums;
}

.caption {
  padding: var(--space-xs) var(--space-s);

  text-align: start;
  font-size: var(--size-100);

  color: var(--text-muted);
}

.empty {
  padding: var(--space-l) var(--space-s);

  text-align: center;

  color: var(--text-muted);
}

/*
 * The edge of a table that continues. It fades into a shade below the page
 * ground, so a cut-off column reads as hidden rather than finished, and it
 * appears only on the side that is actually holding something back.
 */
.fade {
  position: absolute;
  inset-block: 1px;
  z-index: 3;

  inline-size: var(--space-xl);

  opacity: 0;

  pointer-events: none;

  transition: opacity var(--duration-normal) var(--ease);
}

.fadeStart {
  inset-inline-start: 1px;

  background-image: linear-gradient(to right, var(--edge-shade), transparent);

  border-start-start-radius: inherit;
  border-end-start-radius: inherit;
}

.fadeEnd {
  inset-inline-end: 1px;

  background-image: linear-gradient(to left, var(--edge-shade), transparent);

  border-start-end-radius: inherit;
  border-end-end-radius: inherit;
}

.frame[data-start] .fadeStart,
.frame[data-end] .fadeEnd {
  opacity: 0.85;
}

@media (prefers-reduced-motion: reduce) {
  .fade,
  .grip::after {
    transition: none;
  }
}
